// İSG uzmanı portalı — Güvenlik masası "Saha Girişi" (`/security`, S32b `docs/reviews/
// S32b-gate-desk-relayout.md` Option A). Kabuk: Breadcrumb + PageHeader başlık bloğu + yapışkan
// bağlam çubuğu (saha seçici + bağlantı göstergesi + tam ekran) + saha-kapsamlı oturum
// (`SecuritySiteSession`, `key={activeSite.uuid}` — bkz. o dosyanın başlığı, §2.4 kural 9).
//
// **O1/O2/O3/O4 (owner talimatları, S32b §1) burada uygulanır:**
//   - O1: sol sütun ARTIK yalnız "Bugün" TABLOSU — sekme çubuğu KALKTI (`DayListTab.tsx`).
//   - O2: panel dili (küçültülmüş yarıçap, ikonlu başlık, tutarlı başlık yüksekliği) —
//     `PanelHeader.tsx`, `rounded-sm` (bkz. `tokens.css` `--radius-sm`, S32b §3.1).
//   - O3: sağ sütun QR + T.C. Kimlik TEK sekme grubunda, QR VARSAYILAN (`EntryPanel.tsx`).
//   - O4: "Son geçişler" rayı SİLİNDİ (`RecentScansRail.tsx`, `lib/recentScans.ts`) — günlük
//     sayaçlar `DayListTab`'e, "bir kararın kaybolmaması" garantisi `VerdictPanel`+güncel
//     `GET /security/entries?result=all`'a taşındı (D3 fix, bkz. `SecuritySiteSession.tsx`).
//
// **`h1` eklendi (D6 fix, §3.4).** Sayfa eskiden `h2` başlıklar altında hiçbir `h1` TAŞIMIYORDU.
// **S33-8 review fix #4 — bu `h1` artık depo geneli `Breadcrumb`+`PageHeader` anatomisi** (o
// zamanki hand-rolled ikon+başlık+"/"+saha-adı satırının YERİNE) — bkz. aşağıdaki `content`'in
// başındaki not; site adı artık `PageHeader`'ın alt başlık slotunda, `text-primary` riski hiç
// yok (zaten `helperText` `text-muted`).
//
// **`--z-modal` DEFEKTİ — S32d fix (F6, `docs/reviews/S32d-verdict-and-detail.md` §7.2).** Tam
// ekran kabuğu ESKİDEN bu katmanı bir VARDİYA boyunca tutuyordu; "sayfa hiçbir `Modal` AÇMAZ"
// diye ZARARSIZ sayılıyordu (S32b §7.2 D5). O varsayım S32d'nin "Bugün" satırı detay modalıyla
// (Part Two, bu dilimde DEĞİL) ÇÜRÜYOR — bu yüzden kabuk ÖNCEDEN `--z-base`'e indirildi: bir
// `Modal` açıldığında `Dialog.Overlay` (`--z-modal`, 60) kabuğun (artık 0) ÜSTÜNE gelir ve
// perde ÇALIŞIR; `Dialog.Content` (`--z-modal`, 60) hâlâ HER ŞEYİN üstünde, DOM sırasına bağımlı
// bir eşitlik YOK. Doğrulandı: `AppHeader` `position` TAŞIMAYAN düz bir `<header>`, kenar çubuğu
// STATİK bir `<aside>` (`AppLayout.tsx:85`) — `fixed` + `z-index:0` bu kabuk yine de İKİSİNİN de
// üstünde kalır (POZİSYONLU o, onlar DEĞİL). Sayfanın kendi yapışkan bağlam çubuğu
// `--z-dropdown` kabuğun KENDİ yığılma bağlamı İÇİNDE olduğundan ETKİLENMEZ.

import {
  Alert,
  Breadcrumb,
  Button,
  PageHeader,
  type SecuritySiteDto,
  Select,
  useSiteContext,
} from '@isgportal/shared';
import { Maximize2, Minimize2, WifiOff } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOnlineStatus } from '../../lib/useOnlineStatus';
import { SecuritySiteSession } from './SecuritySiteSession';

interface GateConnectivity {
  gateSessionOpen: boolean;
  connectionOk: boolean;
}

const IDLE_CONNECTIVITY: GateConnectivity = { gateSessionOpen: false, connectionOk: true };

export function SecurityDeskPage({ sites }: { sites: SecuritySiteDto[] }) {
  const { t } = useTranslation();
  const browserOnline = useOnlineStatus();

  // S34-3 — paylaşılan `useSiteContext` store'una TAŞINDI (design review M4); bu sayfa hâlâ
  // KENDİ seçicisini render eder, yalnız okuma/yazma artık `apps/web-expert/src/lib/
  // securitySiteStorage.ts` (SİLİNDİ) yerine `@isgportal/shared`'daki paylaşılan store'a gider
  // — bir SONRAKİ tüketici (Panel'in saha seçicisi, S34-4) AYNI seçimi okuyup yazabilsin diye.
  const selectedSite = useSiteContext((s) => s.site);
  const setSelectedSite = useSiteContext((s) => s.setSite);
  const [shellFullScreen, setShellFullScreen] = useState(false);
  const [gateConnectivity, setGateConnectivity] = useState<GateConnectivity>(IDLE_CONNECTIVITY);

  // İlk yükleme: tek sahaysa otomatik seç; değilse son kullanılanı geri yükle (hâlâ listedeyse).
  // İLK SAHAYI SESSİZCE SEÇMEK YOK (S32 §8.5) — yanlış saha `OutOfSite` reddi üretir ve bu
  // işçinin hatası gibi görünür, oysa güvenliğin seçimidir.
  //
  // **Store artık PAYLAŞILAN — `selectedSite` mount anında ZATEN storage'dan dolu gelebilir**
  // (eski local `useState(null)` başlangıcının AKSİNE). Bu yüzden burada, eski davranıştan
  // farklı olarak, dolu bir `selectedSite`i de bu OTURUMUN `sites` listesine karşı doğrularız —
  // aksi hâlde başka bir firmadan/silinmiş bir sahadan kalan bir UUID sessizce "seçili" görünüp
  // `SecuritySiteSession`i geçersiz bir sahayla açardı.
  useEffect(() => {
    const stillValid = selectedSite ? sites.some((s) => s.uuid === selectedSite.uuid) : false;
    if (selectedSite && stillValid) {
      return;
    }
    const only = sites.length === 1 ? sites[0] : undefined;
    if (only) {
      setSelectedSite({ uuid: only.uuid, name: only.name });
      return;
    }
    if (selectedSite && !stillValid) {
      // Kalıcılaştırılmış seçim bu oturumun granted sahaları arasında YOK (S32 §8.5 ile aynı
      // gerekçe: sessizce yanlış sahaya düşmek yerine masa yeniden sorar).
      setSelectedSite(null);
    }
  }, [sites, selectedSite, setSelectedSite]);

  function handleSiteChange(uuid: string) {
    const site = sites.find((s) => s.uuid === uuid);
    if (!site) {
      return;
    }
    // `SecuritySiteSession` eski sahanın örneğiyle birlikte YOK OLUR (`key={uuid}`, bkz. o
    // dosyanın başlığı) — açık kapı oturumu/sahnedeki karar/TC taslağı elle TEMİZLENMEZ, YAPISAL
    // olarak var olmaz. Yalnız buradaki GÖSTERİM state'i (bağlantı göstergesi) senkron sıfırlanır
    // — yeni oturumun kendi ilk raporunu göndermesini BEKLEMEDEN eski sahanın durumunu göstermez.
    setGateConnectivity(IDLE_CONNECTIVITY);
    // `setSelectedSite` (paylaşılan store) kalıcılaştırmayı KENDİSİ yapar (dosya başlığı notu).
    setSelectedSite({ uuid: site.uuid, name: site.name });
  }

  const onConnectivityChange = useCallback((state: GateConnectivity) => {
    setGateConnectivity(state);
  }, []);

  // S32 §8.8 — "driven by `navigator.onLine` plus the outcome of the last request". TÜRETİM
  // AYNI (`useOnlineStatus.ts`'in dosya başı notuyla): kapı oturumu KAPALIYKEN yalnız tarayıcı
  // sinyali; AÇIKKEN son yoklamanın sonucu da.
  //
  // **İkinci tur, owner canlı-kullanım bulgusu — GÖSTERİM değişti, türetim DEĞİL.** Sabit yeşil
  // "Bağlı" rozeti KALKTI: "it tells the guard nothing on the only path where it is true" —
  // güvenli/yaygın durumda hiçbir şey RENDER edilmez. `!connectionOk` iken güçlendirilmiş bir
  // `Alert tone="danger"` çıkar (eski sessiz kırmızı nokta+küçük metin DEĞİL) — sebep somut:
  // yoklama dururken kapı kodu SESSİZCE dönmeyi bırakır, ekranda ölü bir kod bir sıraya
  // gösterilmiş olur. Kapı oturumu AÇIKKEN metin bunu AÇIKÇA söyler (`offlineGateStale`); KAPALI
  // iken genel bir uyarıya düşer (`offline`) — `GateStage.tsx` QR'ın KENDİSİNİN yanında AYNI
  // güçlendirilmiş deseni taşır (bu sayfanın tek nüshası değildi, orada da AYNI gürültü vardı).
  const connectionOk =
    browserOnline && (!gateConnectivity.gateSessionOpen || gateConnectivity.connectionOk);

  // **Güvenlik düzeltmesi (security review, S34-3 sonrası).** `selectedSite` paylaşılan
  // store'dan (localStorage) gelir — çağıranın dokunabileceği, KANITLANMAMIŞ bir kaynak.
  // Yukarıdaki `useEffect` onu `sites`e göre doğrular ama bu render İLE o efekt arasında (aynı
  // tick, efekt henüz koşmadan) `selectedSite` hâlâ eski/yabancı bir UUID taşıyabilir. `content`
  // bu yüzden `selectedSite`i DOĞRUDAN KULLANMAZ — yalnız `sites` (API'den, güvenilir) içinde
  // GERÇEKTEN bulunan girişi (`activeSite`) render eder; bulunamazsa (henüz doğrulanmadı, silinmiş
  // saha, başka firmadan kalma UUID) oturum hiç MONTE EDİLMEZ ve saha adı hiçbir yerde
  // GÖSTERİLMEZ — `PageHeader`'ın `id`si ve `SecuritySiteSession`'a giden `site` HER İKİSİ de bu
  // `sites`-kaynaklı nesneden gelir, kalıcılaştırılmış adın KENDİSİNDEN asla değil.
  const activeSite = selectedSite ? sites.find((s) => s.uuid === selectedSite.uuid) : undefined;

  const content = (
    <div className="flex flex-col gap-4">
      {/* S33-8 review fix #4 — eski hand-rolled `h1` satırı (ikon + başlık + "/" + saha adı)
          yerine depo geneli Breadcrumb + PageHeader anatomisi. Saha adı KULLANICI-TÜRETİLİDİR;
          Pass B `security.subtitle` eklenince `helperText` slotu (her diğer kabuğun statik alt
          başlığıyla AYNI rol) o metne gitti, saha adı `id` slotuna (`PageHeader`'ın "başlığın
          altındaki tanımlayıcı" satırı — permit kodu ile AYNI rol) taşındı; ikisi de KAYBOLMADI,
          sticky bağlam çubuğundaki saha seçici zaten seçili sahayı AYRICA gösterir. */}
      <Breadcrumb items={[{ label: t('security.nav') }]} />
      <PageHeader
        title={t('security.nav')}
        id={activeSite?.name}
        helperText={t('security.subtitle')}
      />

      <div className="sticky top-0 z-[var(--z-dropdown)] flex flex-wrap items-center justify-between gap-3 rounded-sm border border-border bg-surface px-4 py-3">
        <div className="flex flex-wrap items-center gap-4">
          {sites.length > 0 ? (
            <div className="w-56">
              <Select
                aria-label={t('security.context.siteSelect')}
                value={activeSite?.uuid ?? ''}
                placeholder={t('security.context.sitePlaceholder')}
                options={sites.map((s) => ({ value: s.uuid, label: s.name }))}
                onChange={(e) => handleSiteChange(e.target.value)}
              />
            </div>
          ) : null}
          {!connectionOk ? (
            <Alert tone="danger" className="flex items-center gap-1.5 px-2.5 py-1 text-xs">
              <WifiOff className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {gateConnectivity.gateSessionOpen
                ? t('security.context.offlineGateStale')
                : t('security.context.offline')}
            </Alert>
          ) : null}
        </div>
        <Button variant="ghost" size="sm" onClick={() => setShellFullScreen((v) => !v)}>
          {shellFullScreen ? (
            <Minimize2 className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Maximize2 className="h-4 w-4" aria-hidden="true" />
          )}
          <span className="hidden sm:inline">
            {shellFullScreen
              ? t('security.context.exitFullScreen')
              : t('security.context.fullScreen')}
          </span>
        </Button>
      </div>

      {sites.length === 0 ? (
        <div className="rounded-sm border border-border bg-surface p-8 text-center">
          <p className="text-sm text-muted">{t('security.noSites.body')}</p>
        </div>
      ) : !activeSite ? (
        <div className="rounded-sm border border-border bg-surface p-8 text-center">
          <p className="text-sm text-muted">{t('security.noSiteSelected.body')}</p>
        </div>
      ) : (
        <SecuritySiteSession
          key={activeSite.uuid}
          site={activeSite}
          onConnectivityChange={onConnectivityChange}
        />
      )}
    </div>
  );

  if (shellFullScreen) {
    return (
      <div className="fixed inset-0 z-[var(--z-base)] overflow-y-auto bg-bg p-4 sm:p-6">
        {content}
      </div>
    );
  }
  return content;
}
